import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CustomerSearchSelect } from '@/components/ui/customer-search-select';
import { Receipt, Plus, Trash2, FileText, Database } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYS_LABEL = { DENTOTAR: 'DENTOTAR (Privat)', TARIF222: 'Tarif 222 (UV/MV/IV)' };

const DentalBillingPage = () => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [tariffs, setTariffs] = useState([]);
  const [settings, setSettings] = useState({ dentotar_tpw: 1.0, tarif222_tpw: 1.0 });
  const [system, setSystem] = useState('DENTOTAR');
  const [selTariff, setSelTariff] = useState('');
  const [minutes, setMinutes] = useState('');
  const [qty, setQty] = useState('1');
  const [itemNote, setItemNote] = useState('');
  const [items, setItems] = useState([]);       // {tariff, minutes?, quantity?, note}
  const [materials, setMaterials] = useState([]);
  const [matDesc, setMatDesc] = useState('');
  const [matAmount, setMatAmount] = useState('');
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);

  const loadTariffs = useCallback(async () => {
    try { const r = await axios.get(`${API}/dental/billing/tariffs`); setTariffs(r.data?.items || []); }
    catch { toast.error('Tarife konnten nicht geladen werden'); }
  }, []);

  useEffect(() => {
    axios.get(`${API}/customers`).then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.customers || []))).catch(() => {});
    axios.get(`${API}/dental/billing/settings`).then((r) => setSettings(r.data)).catch(() => {});
    loadTariffs();
  }, [loadTariffs]);

  const tariffsForSystem = useMemo(() => tariffs.filter((t) => t.system === system && t.active !== false), [tariffs, system]);
  const selected = useMemo(() => tariffs.find((t) => t.id === selTariff), [tariffs, selTariff]);
  const tpwFor = (sys) => (sys === 'TARIF222' ? settings.tarif222_tpw : settings.dentotar_tpw);

  const lineAmount = (it) => {
    const t = it.tariff;
    const tpw = tpwFor(t.system);
    const tp = Number(t.tax_points || 0);
    const unit = +(tp * tpw).toFixed(2);
    if (t.is_time_based) {
      const per = Math.max(1, Number(t.minutes_per_unit || 5));
      const units = Math.max(1, Math.ceil(Number(it.minutes || 0) / per));
      return +(unit * units).toFixed(2);
    }
    return +(unit * Number(it.quantity || 1)).toFixed(2);
  };

  const servicesTotal = items.reduce((s, it) => s + lineAmount(it), 0);
  const materialsTotal = materials.reduce((s, m) => s + Number(m.amount || 0), 0);
  const grandTotal = +(servicesTotal + materialsTotal).toFixed(2);

  const addItem = () => {
    if (!selected) { toast.error('Bitte Tarifposition wählen'); return; }
    if (selected.is_time_based && !(Number(minutes) > 0)) { toast.error('Minuten erforderlich'); return; }
    setItems([...items, {
      tariff: selected,
      minutes: selected.is_time_based ? Number(minutes) : null,
      quantity: selected.is_time_based ? null : Number(qty || 1),
      note: itemNote.trim() || null,
    }]);
    setSelTariff(''); setMinutes(''); setQty('1'); setItemNote('');
  };

  const addMaterial = () => {
    if (!matDesc.trim() || !(Number(matAmount) > 0)) { toast.error('Material + Betrag erforderlich'); return; }
    setMaterials([...materials, { description: matDesc.trim(), amount: Number(matAmount), tax_rate: 8.1 }]);
    setMatDesc(''); setMatAmount('');
  };

  const saveSettings = async () => {
    try { const r = await axios.put(`${API}/dental/billing/settings`, settings); setSettings(r.data); toast.success('Taxpunktwerte gespeichert'); }
    catch { toast.error('Speichern fehlgeschlagen'); }
  };

  const seed = async () => {
    try {
      const r = await axios.post(`${API}/dental/billing/tariffs/seed`);
      const c = r.data?.created ?? 0, s = r.data?.skipped ?? 0;
      toast.success(c > 0 ? `${c} Beispiel-Tarife geladen` : `Beispiel-Tarife bereits vorhanden (${s})`);
      loadTariffs();
    } catch { toast.error('Konnte nicht laden'); }
  };

  const createInvoice = async () => {
    if (!customerId) { toast.error('Bitte Patient wählen'); return; }
    if (items.length === 0 && materials.length === 0) { toast.error('Mindestens eine Position'); return; }
    setCreating(true);
    try {
      const payload = {
        customer_id: customerId,
        items: items.map((it) => ({ tariff_id: it.tariff.id, minutes: it.minutes, quantity: it.quantity, note: it.note })),
        materials,
        note,
      };
      const r = await axios.post(`${API}/dental/billing/invoice`, payload);
      toast.success(`Rechnung ${r.data?.invoice_number} erstellt (Entwurf)`);
      setItems([]); setMaterials([]); setNote('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rechnung fehlgeschlagen'); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-6" data-testid="dental-billing-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Receipt className="text-teal-600" size={24} /> Dental Abrechnung</h1>
          <p className="text-sm text-slate-500">Tarifpositionen (DENTOTAR / Tarif 222) × Taxpunktwert → QR-Rechnung (Entwurf).</p>
        </div>
        <Button variant="outline" onClick={seed} data-testid="billing-seed-btn"><Database size={16} className="mr-1" /> Beispiel-Tarife laden</Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Taxpunktwerte (pro Praxis)</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div><Label>DENTOTAR TPW (CHF)</Label><Input type="number" step="0.01" value={settings.dentotar_tpw} onChange={(e) => setSettings({ ...settings, dentotar_tpw: +e.target.value })} className="w-36" data-testid="billing-tpw-dentotar" /></div>
          <div><Label>Tarif 222 TPW (CHF)</Label><Input type="number" step="0.01" value={settings.tarif222_tpw} onChange={(e) => setSettings({ ...settings, tarif222_tpw: +e.target.value })} className="w-36" data-testid="billing-tpw-tarif222" /></div>
          <Button variant="outline" onClick={saveSettings} data-testid="billing-tpw-save">Speichern</Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <Label>Patient</Label>
          <div className="max-w-md"><CustomerSearchSelect customers={customers} value={customerId} onValueChange={setCustomerId} placeholder="Patient suchen..." /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Position hinzufügen</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div><Label>Tarif</Label>
            <Select value={system} onValueChange={(v) => { setSystem(v); setSelTariff(''); }}>
              <SelectTrigger className="w-52" data-testid="billing-system-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="DENTOTAR">DENTOTAR (Privat)</SelectItem><SelectItem value="TARIF222">Tarif 222 (UV/MV/IV)</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Position</Label>
            <Select value={selTariff} onValueChange={setSelTariff}>
              <SelectTrigger className="w-72" data-testid="billing-tariff-select"><SelectValue placeholder="Position wählen…" /></SelectTrigger>
              <SelectContent>
                {tariffsForSystem.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Keine Tarife – „Beispiel-Tarife laden"</div>}
                {tariffsForSystem.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`billing-tariff-opt-${t.code}`}>{t.code} · {t.description}{t.is_time_based ? ' ⏱' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selected?.is_time_based ? (
            <div><Label>Minuten</Label><Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} className="w-24" data-testid="billing-minutes" /></div>
          ) : (
            <div><Label>Anzahl</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="w-24" data-testid="billing-qty" /></div>
          )}
          <div className="flex-1 min-w-[160px]"><Label>Notiz (optional)</Label><Input value={itemNote} onChange={(e) => setItemNote(e.target.value)} data-testid="billing-item-note" /></div>
          <Button onClick={addItem} data-testid="billing-add-item"><Plus size={16} className="mr-1" /> Hinzufügen</Button>
        </CardContent>
      </Card>

      {selected?.requires_consultation && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="billing-consultation-hint">
          Hinweis: Diese Position erfordert laut Tarif eine vorgängige Rücksprache mit dem Versicherer.
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Positionen</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Bezeichnung</TableHead><TableHead>Menge</TableHead><TableHead>TP × TPW</TableHead><TableHead className="text-right">Betrag</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-5">Noch keine Positionen</TableCell></TableRow>}
              {items.map((it, i) => (
                <TableRow key={i} data-testid={`billing-line-${i}`}>
                  <TableCell><Badge variant="outline">{it.tariff.code}</Badge></TableCell>
                  <TableCell>{it.tariff.description}{it.note ? ` – ${it.note}` : ''}</TableCell>
                  <TableCell>{it.tariff.is_time_based ? `${it.minutes} Min.` : `${it.quantity}×`}</TableCell>
                  <TableCell className="text-xs text-slate-500">{it.tariff.tax_points} × {tpwFor(it.tariff.system)}</TableCell>
                  <TableCell className="text-right font-medium">CHF {lineAmount(it).toFixed(2)}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`billing-del-item-${i}`}><Trash2 size={14} /></Button></TableCell>
                </TableRow>
              ))}
              {materials.map((m, i) => (
                <TableRow key={`m${i}`} data-testid={`billing-mat-${i}`}>
                  <TableCell><Badge variant="outline">Material</Badge></TableCell>
                  <TableCell>{m.description}</TableCell>
                  <TableCell>1×</TableCell>
                  <TableCell className="text-xs text-slate-500">inkl. {m.tax_rate}% MwSt</TableCell>
                  <TableCell className="text-right font-medium">CHF {Number(m.amount).toFixed(2)}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" onClick={() => setMaterials(materials.filter((_, j) => j !== i))} data-testid={`billing-del-mat-${i}`}><Trash2 size={14} /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t">
            <div className="flex-1 min-w-[180px]"><Label>Material (optional)</Label><Input value={matDesc} onChange={(e) => setMatDesc(e.target.value)} placeholder="z. B. Prophylaxe-Set" data-testid="billing-mat-desc" /></div>
            <div><Label>Betrag CHF</Label><Input type="number" step="0.05" value={matAmount} onChange={(e) => setMatAmount(e.target.value)} className="w-28" data-testid="billing-mat-amount" /></div>
            <Button variant="outline" onClick={addMaterial} data-testid="billing-add-mat"><Plus size={16} className="mr-1" /> Material</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div><Label>Bemerkung auf der Rechnung</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="billing-note" /></div>
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold text-slate-900" data-testid="billing-total">Total: CHF {grandTotal.toFixed(2)}</div>
            <Button onClick={createInvoice} disabled={creating || !customerId} size="lg" data-testid="billing-create-invoice"><FileText size={18} className="mr-2" /> Rechnung erstellen (Entwurf)</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DentalBillingPage;
